import { useState, useEffect } from "react";
import {
  saveBookmarks,
  deleteBookmarks,
  fetchUserBookmarks,
} from "../lib/api_routes/news";

export const filterNewsBookmarksToCurrentAccount = (
  allBookmarkIds,
  accountId,
) =>
  allBookmarkIds.filter((bm) => {
    const [bookmarkAccountId] = bm.split("-");
    return bookmarkAccountId === String(accountId);
  });

export const useBookmarks = (accountId) => {
  const [bookmarksByAccount, setBookmarksByAccount] = useState({});
  const [bookmarksLoaded, setBookmarksLoaded] = useState(false);

  // Load user bookmarks
  const loadUserBookmarks = async () => {
    try {
      const bookmarkData = await fetchUserBookmarks(accountId);
      let bookmarkIds = [];

      if (bookmarkData) {
        if (Array.isArray(bookmarkData.bookmarks)) {
          bookmarkIds = bookmarkData.bookmarks;
        } else if (Array.isArray(bookmarkData)) {
          bookmarkIds = bookmarkData;
        }
      }

      setBookmarksByAccount((prev) => ({
        ...prev,
        [accountId]: new Set(
          bookmarkIds.map((id) => String(id)).filter(Boolean),
        ),
      }));
      setBookmarksLoaded(true);
    } catch (error) {
      console.error("Error loading bookmarks:", error);
      setBookmarksByAccount((prev) => ({
        ...prev,
        [accountId]: new Set(),
      }));
      setBookmarksLoaded(true);
    }
  };

  // Check if a specific new is bookmarked
  const isBookmarked = (newsId) => {
    const currentBookmarks = bookmarksByAccount[accountId] || new Set();
    const newsIdStr = String(newsId);
    const result = currentBookmarks.has(newsIdStr);
    return result;
  };

  // Toggle bookmark
  const toggleBookmark = async (newsId, onSuccess) => {
    const newsIdStr = String(newsId);
    const currentBookmarks = bookmarksByAccount[accountId] || new Set();
    const isCurrentlyBookmarked = currentBookmarks.has(newsIdStr);

    // Update optimistic UI for better UX
    setBookmarksByAccount((prev) => {
      const newState = { ...prev };
      const userBookmarks = new Set(newState[accountId] || []);

      if (isCurrentlyBookmarked) {
        userBookmarks.delete(newsIdStr);
        console.log("Removing bookmark from UI");
      } else {
        userBookmarks.add(newsIdStr);
        console.log("Adding bookmark to UI");
      }
      newState[accountId] = userBookmarks;
      return newState;
    });

    try {
      if (isCurrentlyBookmarked) {
        // Remove bookmark
        const success = await deleteBookmarks(newsId);
        if (!success) {
          console.error("Failed to delete bookmark, rolling back");
          setBookmarksByAccount((prev) => {
            const newState = { ...prev };
            const userBookmarks = new Set(newState[accountId] || []);
            userBookmarks.add(newsIdStr);
            newState[accountId] = userBookmarks;
            return newState;
          });
        } else {
          console.log("Successfully deleted bookmark");
          onSuccess?.();
        }
      } else {
        const result = await saveBookmarks(newsId);
        if (!result?.success) {
          console.error("Failed to save bookmark, rolling back");
          setBookmarksByAccount((prev) => {
            const newState = { ...prev };
            const userBookmarks = new Set(newState[accountId] || []);
            userBookmarks.delete(newsIdStr);
            newState[accountId] = userBookmarks;
            return newState;
          });
        }
      }
    } catch (error) {
      console.error("Error toggling bookmark:", error);
      setBookmarksByAccount((prev) => {
        const newState = { ...prev };
        const userBookmarks = new Set(newState[accountId] || []);
        if (isCurrentlyBookmarked) {
          userBookmarks.add(newsIdStr);
        } else {
          userBookmarks.delete(newsIdStr);
        }
        newState[accountId] = userBookmarks;
        return newState;
      });
    }
  };

  // Load bookmarks on component mount
  useEffect(() => {
    if (!bookmarksLoaded && accountId) {
      loadUserBookmarks();
    }
  }, [accountId, bookmarksLoaded]);

  return {
    bookmarksByAccount,
    bookmarksLoaded,
    toggleBookmark,
    loadUserBookmarks,
    isBookmarked,
  };
};
